import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { RegistryPager, useRegistryPage, useSelectedRecordParam } from './registryPaging';
import {
  useAnnotateVolumeDiscrepancyMutation,
  useCreateSmetaRequestMutation,
  useCreateVolumeCheckMutation,
  useDeleteVolumeCheckMutation,
  useGetVolumeCheckQuery,
  useLazyExportVolumeCheckXlsxQuery,
  useListSmetaContextOptionsQuery,
  useListVolumeChecksQuery,
} from '@shared/api';
import type { VolumeDiscrepancy, VolumeItem } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Input, Select, Label, Field } from '@shared/ui/Input';
import { useAgentPermission } from '@entities/user';
import { useAppSelector } from '@app/hooks';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import {
  decisionLabel,
  fmtNum,
} from '@shared/lib/smetaLabels';
import { DecisionSaveCaption } from './DecisionSaveError';
import { useDecisionSave } from './decisionSave';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Row = styled.div<{ $wrap?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  ${({ $wrap }) => ($wrap ? 'flex-wrap: wrap;' : '')}
`;

const ContextSelect = styled(Select)`
  flex: 1;
  min-width: 220px;
`;

const SubmitWrap = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const CheckRow = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  cursor: pointer;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  font: inherit;
  color: inherit;
  transition: border-color 120ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
`;

const ObjName = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Muted = styled.div<{ $mt?: number }>`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  ${({ $mt }) => ($mt !== undefined ? `margin-top: ${$mt}px;` : '')}
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
  }

  th.num {
    text-align: right;
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

const TableTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-top: ${({ theme }) => theme.spacing(5)};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const FileHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Err = styled.div<{ $mt?: number }>`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  ${({ $mt }) => ($mt !== undefined ? `margin-top: ${$mt}px;` : '')}
`;

const FailedSourcesBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  border-left-width: 4px;
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
  line-height: 1.5;
`;

const FailedSourcesTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.status.warning};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const FailedSourcesList = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(4)};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

function extractFailedSources(summary: unknown): {
  failedSources: string[];
  totalFiles: number | null;
} {
  if (!summary || typeof summary !== 'object') {
    return { failedSources: [], totalFiles: null };
  }
  const rec = summary as Record<string, unknown>;
  const rawList = Array.isArray(rec.failed_sources) ? rec.failed_sources : [];
  const failedSources = rawList
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .map((v) => v.trim());
  const totalFiles =
    typeof rec.files === 'number' && Number.isFinite(rec.files) ? rec.files : null;
  return { failedSources, totalFiles };
}

function statusTone(status: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'processing':
      return 'info';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'completed':
      return 'готово';
    case 'processing':
      return 'анализ…';
    case 'pending':
      return 'в очереди';
    case 'failed':
      return 'ошибка';
    default:
      return status;
  }
}

function ItemsTable({ items }: { items: VolumeItem[] }) {
  if (items.length === 0) {
    return <Muted $mt={8}>Позиции не извлечены.</Muted>;
  }
  return (
    <Table>
      <thead>
        <tr>
          <th>Наименование</th>
          <th>Ед.</th>
          <th className="num">Кол-во</th>
          <th>Раздел</th>
          <th>Источник</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => (
          <tr key={it.id}>
            <td>{it.name}</td>
            <td>{it.unit ?? '—'}</td>
            <td className="num">{fmtNum(it.quantity)}</td>
            <td>{it.section ?? '—'}</td>
            <td>
              <Muted>{it.source_ref ?? '—'}</Muted>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function discrTone(sev: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  switch (sev) {
    case 'critical':
      return 'danger';
    case 'major':
      return 'warning';
    case 'minor':
      return 'info';
    default:
      return 'neutral';
  }
}

function kindLabel(kind: string): string {
  switch (kind) {
    case 'missing_in_contract':
      return 'нет в контракте';
    case 'missing_in_rd':
      return 'нет в РД';
    case 'qty_mismatch':
      return 'кол-во';
    case 'unit_mismatch':
      return 'единицы';
    default:
      return kind;
  }
}

const DecisionBtns = styled.div`
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

function fmtDelta(d: VolumeDiscrepancy): string {
  if (d.delta === null || d.delta === undefined) return '—';
  const pct = d.delta_pct !== null && d.delta_pct !== undefined ? ` (${d.delta_pct.toFixed(1)}%)` : '';
  return `${fmtNum(d.delta)}${pct}`;
}

function Discrepancies({ items, checkId }: { items: VolumeDiscrepancy[]; checkId: string }) {
  const [annotate, { isLoading }] = useAnnotateVolumeDiscrepancyMutation();
  const { error: saveError, save } = useDecisionSave();
  const { can_write } = useAgentPermission('smeta');
  if (items.length === 0) {
    return <Muted $mt={8}>Расхождений не найдено.</Muted>;
  }
  return (
    <Table>
      <DecisionSaveCaption message={saveError} />
      <thead>
        <tr>
          <th>Важность</th>
          <th>Тип</th>
          <th>Наименование</th>
          <th>Ед.</th>
          <th className="num">РД</th>
          <th className="num">Контракт</th>
          <th className="num">Δ</th>
          <th>Решение</th>
        </tr>
      </thead>
      <tbody>
        {items.map((d) => (
          <tr key={d.id}>
            <td>
              <Badge $tone={discrTone(d.severity)}>{d.severity}</Badge>
            </td>
            <td>{kindLabel(d.kind)}</td>
            <td>
              {d.name}
              <Muted>{d.description}</Muted>
              {d.sheets_note && <Muted>{d.sheets_note}</Muted>}
            </td>
            <td>{d.unit ?? '—'}</td>
            <td className="num">{fmtNum(d.rd_quantity)}</td>
            <td className="num">{fmtNum(d.contract_quantity)}</td>
            <td className="num">{fmtDelta(d)}</td>
            <td>
              {d.decision ? (
                <Badge
                  $tone={
                    d.decision === 'accepted'
                      ? 'success'
                      : d.decision === 'rejected'
                        ? 'neutral'
                        : 'warning'
                  }
                >
                  {decisionLabel(d.decision)}
                </Badge>
              ) : (
                <DecisionBtns>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => void save(() => annotate({ id: d.id, checkId, decision: 'accepted' }))}
                  >
                    Принять
                  </Button>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => void save(() => annotate({ id: d.id, checkId, decision: 'disputed' }))}
                  >
                    Спорно
                  </Button>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => void save(() => annotate({ id: d.id, checkId, decision: 'rejected' }))}
                  >
                    Отклонить
                  </Button>
                </DecisionBtns>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function VolumeChecks() {
  const [objectName, setObjectName] = useState('');
  const [contractId, setContractId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [rdFiles, setRdFiles] = useState<File[]>([]);
  const [contractFiles, setContractFiles] = useState<File[]>([]);
  const [selectedId, setSelectedId] = useSelectedRecordParam();
  const [poll, setPoll] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [requestNotice, setRequestNotice] = useState<string | null>(null);
  const { can_write } = useAgentPermission('smeta');
  const currentUserId = useAppSelector((s) => s.auth.user?.id);
  const { activeProjectId, activeContractId } = useActiveProjectContext();

  const page = useRegistryPage();
  const listQ = useListVolumeChecksQuery(
    { limit: page.limit, offset: page.offset },
    { pollingInterval: 5000 },
  );
  const contextOptionsQ = useListSmetaContextOptionsQuery();
  const [createCheck, { isLoading: creating }] = useCreateVolumeCheckMutation();
  const [deleteCheck] = useDeleteVolumeCheckMutation();
  const [createRequest, { isLoading: sendingRequest }] = useCreateSmetaRequestMutation();
  const detailQ = useGetVolumeCheckQuery(selectedId ?? '', {
    skip: !selectedId,
    pollingInterval: poll,
  });
  const [exportXlsx, { isFetching: exporting }] = useLazyExportVolumeCheckXlsxQuery();
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId && activeProjectId) {
      setProjectId(activeProjectId);
      const found = contextOptionsQ.data?.projects.find((p) => p.id === activeProjectId);
      if (!objectName.trim() && found?.name) {
        setObjectName(found.name);
      }
    }
    if (!contractId && activeContractId) {
      setContractId(activeContractId);
    }
  }, [
    activeProjectId,
    activeContractId,
    contextOptionsQ.data?.projects,
    projectId,
    contractId,
    objectName,
  ]);

  // Поллим карточку, пока проверка в работе; останавливаем на завершении.
  useEffect(() => {
    if (!selectedId) {
      setPoll(0);
      return;
    }
    const st = detailQ.data?.check.status;
    setPoll(st === 'pending' || st === 'processing' ? 2500 : 0);
  }, [selectedId, detailQ.data?.check.status]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (rdFiles.length === 0 && contractFiles.length === 0) {
      setError('Приложите хотя бы один файл — РД или контракт.');
      return;
    }
    const fd = new FormData();
    fd.append('object_name', objectName);
    if (contractId) fd.append('contract_id', contractId);
    if (projectId) fd.append('project_id', projectId);
    rdFiles.forEach((f) => fd.append('rd', f));
    contractFiles.forEach((f) => fd.append('contract', f));
    try {
      const res = await createCheck(fd).unwrap();
      setSelectedId(res.check_id);
      setObjectName('');
      setRdFiles([]);
      setContractFiles([]);
    } catch (err) {
      setError('Не удалось запустить проверку. Проверьте формат файлов и попробуйте снова.');
      // eslint-disable-next-line no-console
      console.error('createVolumeCheck failed', err);
    }
  }

  async function handleSendRevisionRequest() {
    if (!detail) return;
    setRequestNotice(null);
    setExportError(null);
    try {
      await createRequest({
        kind: 'contract_price_revision',
        contract_id: detail.check.contract_id ?? (contractId || undefined),
        project_id: detail.check.project_id ?? (projectId || undefined),
        title: `Пересмотр ТЦ / ВОР по сверке объёмов: ${detail.check.object_name}`,
        summary: `По результатам сверки РД ↔ ВОР на объекте «${detail.check.object_name}» выявлено расхождений: ${detail.discrepancies.length}. Требуется корректировка ВОР / ДС.`,
        details: {
          volume_check_id: detail.check.id,
          object_name: detail.check.object_name,
          discrepancy_count: detail.discrepancies.length,
        },
      }).unwrap();
      setRequestNotice('Заявка на пересмотр ТЦ / ВОР отправлена в Юрслужбу и ПТО.');
    } catch {
      setExportError('Не удалось отправить заявку на пересмотр ТЦ / ВОР.');
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteCheck(id).unwrap();
      if (selectedId === id) setSelectedId(null);
    } catch {
      // ignore — список обновится сам при следующем поллинге
    }
  }

  // Выгрузка XLSX-отчёта о проверке объёмов (Функция 1). Доступна после
  // завершения проверки; имя файла backend формирует ASCII-safe.
  async function handleExport(id: string) {
    setExportError(null);
    try {
      const blob = await exportXlsx(id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `volume_check_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError('Не удалось выгрузить отчёт XLSX.');
      // eslint-disable-next-line no-console
      console.error('exportVolumeCheckXlsx failed', err);
    }
  }

  const checks = listQ.data?.items ?? [];
  const contextContracts = contextOptionsQ.data?.contracts ?? [];
  const contextProjects = contextOptionsQ.data?.projects ?? [];
  const detail = detailQ.data;
  const isAuthor =
    !detail?.check.user_id ||
    !currentUserId ||
    detail.check.user_id === currentUserId;

  return (
    <Stack>
      <Card>
        <form onSubmit={handleSubmit}>
          <Field>
            <Label>Объект</Label>
            <Input
              placeholder="Например: Школа на 500 мест, корпус Б"
              value={objectName}
              onChange={(e) => setObjectName(e.target.value)}
            />
          </Field>
          <Field>
            <Label>Привязка к договору Статута и проекту ПТО (необязательно)</Label>
            <Row $wrap>
              <ContextSelect
                value={contractId}
                onChange={(e) => setContractId(e.target.value)}
              >
                <option value="">— договор Статута —</option>
                {contextContracts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.contract_number ? `№ ${c.contract_number}` : 'договор б/н'} ·{' '}
                    {(c.subject || c.counterparty || '').slice(0, 40)}
                  </option>
                ))}
              </ContextSelect>
              <ContextSelect
                value={projectId}
                onChange={(e) => {
                  const id = e.target.value;
                  setProjectId(id);
                  const found = contextProjects.find((p) => p.id === id);
                  if (found?.name && !objectName.trim()) {
                    setObjectName(found.name);
                  }
                }}
              >
                <option value="">— проект ПТО / объект —</option>
                {contextProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code ? `${p.code} · ` : ''}
                    {p.name}
                  </option>
                ))}
              </ContextSelect>
            </Row>
          </Field>
          <Field>
            <Label>Рабочая документация (РД)</Label>
            <Input
              type="file"
              multiple
              accept=".zip,.pdf,.docx,.xlsx"
              onChange={(e) => setRdFiles(Array.from(e.target.files ?? []))}
            />
            <FileHint>
              Обычно ZIP-архив с PDF. Выбрано файлов: {rdFiles.length}
            </FileHint>
          </Field>
          <Field>
            <Label>Контракт / ВОР (РДЦ)</Label>
            <Input
              type="file"
              multiple
              accept=".xlsx,.pdf,.docx"
              onChange={(e) => setContractFiles(Array.from(e.target.files ?? []))}
            />
            <FileHint>Excel, PDF или Word. Выбрано файлов: {contractFiles.length}</FileHint>
          </Field>
          {error && <Err>{error}</Err>}
          <SubmitWrap>
            <Button
              type="submit"
              $variant="primary"
              disabled={creating || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              {creating ? 'Запуск…' : 'Запустить проверку'}
            </Button>
          </SubmitWrap>
        </form>
      </Card>

      <Grid>
        <Stack>
          <RegistryPager
            label="Проверок"
            page={page}
            shown={checks.length}
            hasMore={listQ.data?.has_more}
            fetching={listQ.isFetching}
          />
          {checks.map((c) => {
            const { failedSources: rowFailed } = extractFailedSources(c.summary);
            return (
              <CheckRow key={c.id} $active={c.id === selectedId} onClick={() => setSelectedId(c.id)}>
                <Row>
                  <ObjName>{c.object_name}</ObjName>
                  <Badge $tone={statusTone(c.status)}>{statusLabel(c.status)}</Badge>
                </Row>
                <Row>
                  <Muted>{new Date(c.created_at).toLocaleString('ru-RU')}</Muted>
                  {c.status === 'processing' && <Muted>{c.progress_percent}%</Muted>}
                  {rowFailed.length > 0 && (
                    <Badge $tone="warning">сбоев чтения: {rowFailed.length}</Badge>
                  )}
                </Row>
              </CheckRow>
            );
          })}
          {checks.length === 0 && <Muted>Пока нет проверок. Загрузите документы выше.</Muted>}
        </Stack>

        <div>
          {!selectedId && <Muted>Выберите проверку слева, чтобы увидеть извлечённые позиции.</Muted>}
          {selectedId && !detail && <Muted>Загрузка…</Muted>}
          {detail && (() => {
            const { failedSources, totalFiles } = extractFailedSources(detail.check.summary);
            return (
              <Card>
                <Row>
                  <div>
                    <ObjName>{detail.check.object_name}</ObjName>
                    <Muted>
                      Источников: {detail.sources.length} · РД-позиций: {detail.rd_items.length} ·
                      контракт-позиций: {detail.contract_items.length} · расхождений:{' '}
                      {detail.discrepancies.length}
                    </Muted>
                  </div>
                  <Row>
                    <Badge $tone={statusTone(detail.check.status)}>
                      {statusLabel(detail.check.status)}
                    </Badge>
                    {detail.check.status === 'completed' && (
                      <Button
                        $variant="secondary"
                        $size="sm"
                        type="button"
                        onClick={() => handleExport(detail.check.id)}
                        disabled={exporting}
                      >
                        {exporting ? 'Готовлю…' : 'Сформировать XLSX'}
                      </Button>
                    )}
                    {detail.check.status === 'completed' && detail.discrepancies.length > 0 && (
                      <Button
                        $variant="secondary"
                        $size="sm"
                        type="button"
                        disabled={sendingRequest || !can_write}
                        onClick={handleSendRevisionRequest}
                      >
                        {sendingRequest ? 'Отправляю…' : 'Запросить пересмотр ТЦ / ВОР'}
                      </Button>
                    )}
                    <Button
                      $variant="ghost"
                      $size="sm"
                      type="button"
                      disabled={!can_write || !isAuthor}
                      title={
                        !can_write
                          ? 'Только чтение'
                          : !isAuthor
                            ? 'Удалить проверку может только её автор'
                            : undefined
                      }
                      onClick={() => handleDelete(detail.check.id)}
                    >
                      Удалить
                    </Button>
                  </Row>
                </Row>

                {failedSources.length > 0 && (
                  <FailedSourcesBanner role="alert">
                    <FailedSourcesTitle>
                      Не удалось разобрать файлов: {failedSources.length}
                      {totalFiles !== null ? ` из ${totalFiles}` : ''}. Сверка выполнена только по
                      остальным документам — перепроверьте перечисленные источники:
                    </FailedSourcesTitle>
                    <FailedSourcesList>
                      {failedSources.map((src, idx) => (
                        <li key={`${src}-${idx}`}>{src}</li>
                      ))}
                    </FailedSourcesList>
                  </FailedSourcesBanner>
                )}

                {requestNotice && <Muted $mt={8}>{requestNotice}</Muted>}
                {detail.check.status === 'failed' && detail.check.error_message && (
                  <Err $mt={8}>{detail.check.error_message}</Err>
                )}
                {exportError && <Err $mt={8}>{exportError}</Err>}

                <TableTitle>
                  Расхождения <Badge $tone="neutral">{detail.discrepancies.length}</Badge>
                </TableTitle>
                <Discrepancies items={detail.discrepancies} checkId={detail.check.id} />

                <TableTitle>
                  Рабочая документация (РД) <Badge $tone="neutral">{detail.rd_items.length}</Badge>
                </TableTitle>
                <ItemsTable items={detail.rd_items} />

                <TableTitle>
                  Контракт / ВОР <Badge $tone="neutral">{detail.contract_items.length}</Badge>
                </TableTitle>
                <ItemsTable items={detail.contract_items} />
              </Card>
            );
          })()}
        </div>
      </Grid>
    </Stack>
  );
}
